import React, { useEffect } from "react";

import DotsHorizontal from "@uui/DotsHorizontal";
import PlusSquare from "@uui/PlusSquare";
import SendIcon from "@uui/Send01";
import ZapIcon from "@uui/ZapSquare";
import { FieldValues, useForm } from "react-hook-form";
import * as yup from "yup";

import { Divider, FormHelperText, Typography } from "@mui/material";

import useWebhooks from "@app/analytics/hooks/integrations/useWebhooks";
import { FormSubLabel } from "@app/analytics/Integrations/components/styles";
import { IntegrationMenu } from "@app/analytics/Integrations/components/styles";
import type {
  TestWebhookRequest,
  Webhook,
} from "@app/analytics/types/webhookTypes";
import { toast } from "@app/common/hooks/useToasts";
import { yupResolver } from "@app/utils/forms/yupResolver";
import useAnchorElement from "@app/utils/hooks/useAnchorElement";

import Alert from "@rui/Alert";
import Button from "@rui/Button";
import IconButton from "@rui/IconButton";
import MenuItem from "@rui/MenuItem";
import Modal from "@rui/Modal";
import Stack, { Column, Row } from "@rui/Stack";
import TextField from "@rui/TextField";

import { FormLabel, IconHeading } from "./styles";

const CREATE_SCHEMA = yup
  .object()
  .shape({
    name: yup.string().required(),
    url: yup.string().url().required(),
  })
  .required();

const AddOrEditWebhookModal = ({
  onClose,
  open,
  webhook,
}: {
  onClose: () => void;
  open: boolean;
  webhook?: Webhook;
}) => {
  const {
    anchorElement,
    open: menuOpen,
    onClick,
    onClose: onCloseMenu,
  } = useAnchorElement();

  const { createWebhook, updateWebhook, sendTestRequest } = useWebhooks();

  const editing = Boolean(webhook);

  const defaultValues = {
    name: webhook?.name ?? "",
    url: webhook?.url ?? "",
  };

  const {
    handleSubmit: handleFormSubmit,
    register,
    getValues,
    reset,
    formState: { errors, isValid, dirtyFields },
  } = useForm({
    mode: "onBlur",
    resolver: yupResolver(CREATE_SCHEMA),
  });

  useEffect(() => {
    reset(defaultValues);
  }, [webhook?.id]);

  const handleClose = () => {
    onClose();
    reset(defaultValues);
  };

  const handleCreateWebhook = (data: FieldValues) => {
    createWebhook.mutate(
      { name: data.name, url: data.url },
      {
        onSuccess: () => handleClose(),
        onError: () => {
          handleClose();
          toast.error(
            "Something went wrong and your webhook could not be created. Please try again later or contact support@read.ai if the problem persists."
          );
        },
      }
    );
  };

  const handleUpdateWebhook = (data: FieldValues) => {
    // Don't bother updating anything if user didn't make any changes
    if (!webhook || !dirtyFields) return handleClose();

    // Only update the fields that the user changed
    const dirtyFieldsToUpdate = Object.keys(dirtyFields).reduce(
      (obj, key) => {
        obj[key] = data[key];
        return obj;
      },
      {} as { [key: string]: string }
    );

    updateWebhook.mutate(
      { ...dirtyFieldsToUpdate, id: webhook.id },
      {
        onSuccess: () => handleClose(),
        onError: () => {
          handleClose();
          toast.error(
            "Something went wrong and your changes could not be saved. Please try again later or contact support@read.ai if the problem persists."
          );
        },
      }
    );
  };

  const handleSubmit = (data: FieldValues) => {
    const submitFn = editing ? handleUpdateWebhook : handleCreateWebhook;
    submitFn(data);
  };

  const handleSendTest = () => {
    const valuesToTest = getValues() as TestWebhookRequest;
    sendTestRequest.mutate(valuesToTest);
    onCloseMenu();
  };

  const isError = editing ? updateWebhook.isError : createWebhook.isError;

  return (
    <>
      <Modal onClose={handleClose} open={open}>
        <form onSubmit={handleFormSubmit(handleSubmit)} id="create-webhook">
          {isError && (
            <Alert severity="error" embedded>
              Something went wrong. Check the fields and try again
            </Alert>
          )}
          <Stack spacing={2}>
            <Row variant="apart">
              <Typography variant="h2">
                {editing ? "Edit" : "Add"} webhook
              </Typography>
              <IconButton onClick={onClick}>
                <DotsHorizontal />
              </IconButton>
            </Row>
            <Stack spacing={3} sx={{ width: "100%" }}>
              <Column spacing={1}>
                <Stack>
                  <FormSubLabel>Webhook name (required)</FormSubLabel>
                  <TextField
                    required
                    {...register("name")}
                    placeholder="Push meeting notes"
                  />
                </Stack>
                <Stack>
                  <FormSubLabel
                    sx={{
                      color: errors?.url ? "shades.negative.600" : undefined,
                    }}
                  >
                    HTTPS URL (required)
                  </FormSubLabel>
                  <TextField
                    required
                    {...register("url")}
                    placeholder="https://..."
                  />
                  {errors?.url && (
                    <FormHelperText sx={{ color: "shades.negative.600" }}>
                      Must be a valid https url
                    </FormHelperText>
                  )}
                </Stack>
              </Column>
              <Stack spacing={1}>
                <IconHeading Icon={ZapIcon}>Trigger</IconHeading>
                <Typography variant="bodyPrimaryReg" color="shades.neutral.800">
                  When a meeting ends
                </Typography>
              </Stack>
              <Stack spacing={1}>
                <IconHeading Icon={PlusSquare}>Action(s)</IconHeading>
                <FormLabel
                  title="Send meeting notes & transcript"
                  description="Summary, chapters, topics, action items, key questions, and meeting transcript"
                />
              </Stack>
            </Stack>
            <Divider />
            <Row variant="right" spacing={1}>
              <Button
                variant="text"
                onClick={handleClose}
                sx={{ minWidth: "100px" }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={!isValid}
                loading={
                  editing ? updateWebhook.isPending : createWebhook.isPending
                }
              >
                {editing ? "Save changes" : "Create webhook"}
              </Button>
            </Row>
          </Stack>
        </form>
      </Modal>

      <IntegrationMenu
        id="integration-menu"
        anchorEl={anchorElement}
        open={menuOpen}
        onClose={onCloseMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem
          disabled={!isValid}
          onClick={handleSendTest}
          icon={<SendIcon />}
        >
          Send test request
        </MenuItem>
      </IntegrationMenu>
    </>
  );
};

export default AddOrEditWebhookModal;
